import React, { useState, useCallback, useEffect } from 'react';
import { Clock, Loader2, Save, X } from 'lucide-react';
import api from '../../../services/api';
import LightningIcon from './icons/LightningIcon';
import ClockIcon from './icons/ClockIcon';

const CATEGORY_LABELS = {
    'droitAerien': { fr: 'Droit Aérien', en: 'Air Law' },
    'meteo': { fr: 'Météo', en: 'Meteo' },
    'navigation': { fr: 'Navigation', en: 'Navigation' },
    'connaissance': { fr: 'Conn. Générale', en: 'Gen. Knowledge' },
    'complet': { fr: 'Examen complet', en: 'Full Exam' },
};

const FILTER_TYPES = [
    { id: 'level', label: { fr: 'Tout le niveau', en: 'Full level' } },
    { id: 'category', label: { fr: 'Par catégorie', en: 'By category' } },
];

const REVISION_PRESETS = [
    { id: 30, label: { fr: '30 jours', en: '30 days' }, desc: { fr: 'Pas vues depuis 1 mois', en: 'Not seen in 1 month' } },
    { id: 60, label: { fr: '60 jours', en: '60 days' }, desc: { fr: 'Pas vues depuis 2 mois', en: 'Not seen in 2 months' } },
    { id: 90, label: { fr: '90 jours', en: '90 days' }, desc: { fr: 'Pas vues depuis 3 mois', en: 'Not seen in 3 months' } },
];

const RevisionExamSection = ({ levelId, language, onStartExam, activeExams, requiresUpgrade = false, onUpgrade }) => {
    const [daysThreshold, setDaysThreshold] = useState(30);
    const [customDays, setCustomDays] = useState('');
    const [isCustomMode, setIsCustomMode] = useState(false);
    const [filterType, setFilterType] = useState('level');
    const [filterValue, setFilterValue] = useState([]);
    const [searching, setSearching] = useState(false);
    const [loadingPageIdx, setLoadingPageIdx] = useState(null);
    const [searchResult, setSearchResult] = useState(null);
    const [error, setError] = useState(null);
    const [showModeModal, setShowModeModal] = useState(null);
    const [presets, setPresets] = useState([]);
    const [presetName, setPresetName] = useState('');
    const [showSavePreset, setShowSavePreset] = useState(false);
    const [savingPreset, setSavingPreset] = useState(false);

    // Load presets
    useEffect(() => {
        api.getCustomExamPresets(levelId, 'revision').then(setPresets).catch(() => {});
    }, [levelId]);

    const handleSavePreset = useCallback(async () => {
        const name = presetName.trim();
        if (!name) return;
        setSavingPreset(true);
        try {
            await api.saveCustomExamPreset({
                levelId,
                presetType: 'revision',
                name,
                config: { daysThreshold, filterType, filterValue },
                resultCount: searchResult?.totalQuestions ?? 0
            });
            setPresetName('');
            setShowSavePreset(false);
            const updated = await api.getCustomExamPresets(levelId, 'revision');
            setPresets(updated);
        } catch { /* ignore */ }
        setSavingPreset(false);
    }, [presetName, levelId, daysThreshold, filterType, filterValue, searchResult]);

    const handleDeletePreset = useCallback(async (presetId) => {
        try {
            await api.deleteCustomExamPreset(presetId);
            setPresets(prev => prev.filter(p => p.id !== presetId));
        } catch { /* ignore */ }
    }, []);

    const handleLoadPreset = useCallback((preset) => {
        const config = preset.config;
        const days = config.daysThreshold ?? 30;
        const isPreset = REVISION_PRESETS.some(p => p.id === days);
        setDaysThreshold(days);
        setIsCustomMode(!isPreset);
        setCustomDays(!isPreset ? String(days) : '');
        setFilterType(config.filterType || 'level');
        const fv = config.filterValue;
        setFilterValue(Array.isArray(fv) ? fv : (fv ? [fv] : []));
        setSearchResult(null);
    }, []);

    // Build unique categories from active exams
    const availableCategories = (() => {
        const cats = new Set();
        (activeExams || []).forEach(e => {
            if (e.category && e.category !== 'keywords') cats.add(e.category);
        });
        return Array.from(cats).map(c => ({
            id: c,
            label: CATEGORY_LABELS[c] || { fr: c, en: c }
        }));
    })();

    const getLocalizedText = (textObj) => {
        if (!textObj) return '';
        if (typeof textObj === 'string') return textObj;
        return textObj[language] || textObj['en'] || '';
    };

    const handleSearch = useCallback(async () => {
        setSearching(true);
        setError(null);
        setSearchResult(null);

        try {
            const result = await api.searchRevisionQuestions(
                levelId,
                daysThreshold,
                filterType,
                filterType !== 'level' && filterValue.length > 0 ? filterValue : null
            );
            setSearchResult(result);
        } catch (err) {
            setError(language === 'fr'
                ? 'Erreur lors de la recherche. Veuillez réessayer.'
                : 'Search error. Please try again.');
        } finally {
            setSearching(false);
        }
    }, [levelId, daysThreshold, filterType, filterValue, language]);

    const handleStartExam = useCallback((pageIdx) => {
        const page = searchResult?.pages?.[pageIdx];
        if (!page) return;
        setShowModeModal({ pageIdx, questionIds: page.questionIds });
    }, [searchResult]);

    const handleModeSelected = useCallback(async (mode) => {
        if (!showModeModal) return;
        const { pageIdx, questionIds } = showModeModal;
        setShowModeModal(null);
        setLoadingPageIdx(pageIdx);

        try {
            const questions = await api.getQuestionsByIds(questionIds);
            if (questions.length === 0) {
                setError(language === 'fr'
                    ? 'Aucune question trouvée.'
                    : 'No questions found.');
                return;
            }
            onStartExam(questions, mode, {
                examType: 'revision',
                config: { daysThreshold, filterType, filterValue },
                pageIndex: pageIdx
            });
        } catch (err) {
            setError(language === 'fr'
                ? 'Erreur lors du chargement des questions.'
                : 'Error loading questions.');
        } finally {
            setLoadingPageIdx(null);
        }
    }, [showModeModal, language, onStartExam, daysThreshold, filterType, filterValue]);

    // Filter label for results
    const getFilterLabel = () => {
        if (filterType === 'category' && filterValue.length > 0) {
            return filterValue.map(v => {
                const cat = CATEGORY_LABELS[v];
                return cat ? getLocalizedText(cat) : v;
            }).join(', ');
        }
        return language === 'fr' ? 'Tout le niveau' : 'Full level';
    };

    const getModeLabel = () => {
        return language === 'fr' ? `${daysThreshold}j` : `${daysThreshold}d`;
    };

    return (
        <div data-help="custom-revision" className="space-y-4">
            <h2 className="text-lg font-black text-gray-400 dark:text-white ml-2 text-xs uppercase tracking-widest opacity-60 dark:opacity-40">
                {language === 'fr' ? 'Révision' : 'Revision'}
            </h2>

            {/* Filter Card */}
            <div className="bg-white dark:bg-[#121212] rounded-2xl p-5 border border-gray-200 dark:border-white/10 shadow-sm dark:shadow-none">
                <div className="flex flex-col gap-4">
                    {/* Days threshold toggle */}
                    <div className="flex gap-2">
                        {REVISION_PRESETS.map(rm => (
                            <button
                                key={rm.id}
                                onClick={() => { setDaysThreshold(rm.id); setIsCustomMode(false); setCustomDays(''); setSearchResult(null); }}
                                className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-bold transition-all text-left ${
                                    !isCustomMode && daysThreshold === rm.id
                                        ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 border-2 border-teal-400 dark:border-teal-600'
                                        : 'bg-gray-50 dark:bg-white/5 text-gray-500 dark:text-gray-400 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-white/10'
                                }`}
                            >
                                <div>{getLocalizedText(rm.label)}</div>
                                <div className={`text-xs font-normal mt-0.5 ${!isCustomMode && daysThreshold === rm.id ? 'text-teal-600/70 dark:text-teal-400/60' : 'text-gray-400 dark:text-gray-500'}`}>
                                    {getLocalizedText(rm.desc)}
                                </div>
                            </button>
                        ))}
                        {/* Custom days */}
                        <button
                            onClick={() => { setIsCustomMode(true); setSearchResult(null); }}
                            className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-bold transition-all text-left ${
                                isCustomMode
                                    ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 border-2 border-teal-400 dark:border-teal-600'
                                    : 'bg-gray-50 dark:bg-white/5 text-gray-500 dark:text-gray-400 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-white/10'
                            }`}
                        >
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={customDays}
                                    placeholder="—"
                                    onFocus={() => setIsCustomMode(true)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setCustomDays(val);
                                        setIsCustomMode(true);
                                        const num = parseInt(val, 10);
                                        if (num > 0) {
                                            setDaysThreshold(num);
                                            setSearchResult(null);
                                        }
                                    }}
                                    className={`w-12 bg-transparent outline-none text-sm font-bold ${
                                        isCustomMode ? 'text-teal-700 dark:text-teal-300 placeholder-teal-400/50' : 'text-gray-500 dark:text-gray-400 placeholder-gray-400 dark:placeholder-gray-500'
                                    }`}
                                />
                                <span>{language === 'fr' ? 'jours' : 'days'}</span>
                            </div>
                            <div className={`text-xs font-normal mt-0.5 ${isCustomMode ? 'text-teal-600/70 dark:text-teal-400/60' : 'text-gray-400 dark:text-gray-500'}`}>
                                {language === 'fr' ? 'Définir un nombre de jours' : 'Set a custom number of days'}
                            </div>
                        </button>
                    </div>

                    {/* Scope label */}
                    <div>
                        <p className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
                            {language === 'fr' ? 'Périmètre' : 'Scope'}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {language === 'fr'
                                ? 'Les questions utilisant les annexes ne seront pas incluses. Découpage en examens de 50 questions maximum.'
                                : 'Questions using appendices will not be included. Split into exams of 50 questions max.'}
                        </p>
                    </div>

                    {/* Filter type toggle */}
                    <div className="flex gap-2">
                        {FILTER_TYPES.map(ft => (
                            <button
                                key={ft.id}
                                onClick={() => {
                                    setFilterType(ft.id);
                                    setFilterValue([]);
                                    setSearchResult(null);
                                }}
                                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                                    filterType === ft.id
                                        ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/20'
                                        : 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10'
                                }`}
                            >
                                {getLocalizedText(ft.label)}
                            </button>
                        ))}
                    </div>

                    {/* Category chips */}
                    {filterType === 'category' && availableCategories.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {availableCategories.map(cat => {
                                const selected = filterValue.includes(cat.id);
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => {
                                            setFilterValue(prev => selected ? prev.filter(v => v !== cat.id) : [...prev, cat.id]);
                                            setSearchResult(null);
                                        }}
                                        className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition-all ${
                                            selected
                                                ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700/50 shadow-sm'
                                                : 'bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10'
                                        }`}
                                    >
                                        {getLocalizedText(cat.label)}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Generate / Unlock button */}
                    {requiresUpgrade ? (
                        <button
                            onClick={onUpgrade}
                            className="self-end px-5 py-2.5 rounded-xl font-bold text-sm text-white shadow-lg transition bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                            {language === 'fr' ? 'Débloquer' : 'Unlock'}
                        </button>
                    ) : (
                        <button
                            onClick={handleSearch}
                            disabled={searching || (filterType !== 'level' && filterValue.length === 0) || (isCustomMode && (!customDays || parseInt(customDays, 10) < 1))}
                            className="self-end px-5 py-2.5 rounded-xl font-bold text-sm text-white shadow-lg transition bg-teal-500 hover:bg-teal-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {searching ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Clock className="w-4 h-4" />
                            )}
                            {language === 'fr' ? 'Générer' : 'Generate'}
                        </button>
                    )}

                    {/* Saved presets */}
                    {presets.length > 0 && (
                        <div className="pt-2 border-t border-gray-100 dark:border-white/5">
                            <p className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-2">
                                {language === 'fr' ? 'Configurations sauvegardées' : 'Saved configurations'}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {presets.map((preset) => (
                                    <div key={preset.id} className="inline-flex items-center gap-1">
                                        <button
                                            onClick={() => handleLoadPreset(preset)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-sm font-medium text-gray-700 dark:text-gray-300 transition"
                                        >
                                            {preset.name}
                                            <span className="text-gray-400 dark:text-gray-500">({preset.lastResultCount}q)</span>
                                        </button>
                                        <button
                                            onClick={() => handleDeletePreset(preset.id)}
                                            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Save preset (after results) */}
                    {searchResult && searchResult.totalQuestions > 0 && (
                        <div className="pt-2 border-t border-gray-100 dark:border-white/5">
                            {showSavePreset ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={presetName}
                                        onChange={(e) => setPresetName(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                                        placeholder={language === 'fr' ? 'Nom de la configuration...' : 'Configuration name...'}
                                        className="flex-1 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:border-teal-400"
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleSavePreset}
                                        disabled={!presetName.trim() || savingPreset}
                                        className="px-3 py-1.5 rounded-lg bg-teal-500 text-white text-xs font-bold disabled:opacity-40 hover:bg-teal-600 transition"
                                    >
                                        {savingPreset ? <Loader2 className="w-3 h-3 animate-spin" /> : (language === 'fr' ? 'Enregistrer' : 'Save')}
                                    </button>
                                    <button
                                        onClick={() => setShowSavePreset(false)}
                                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 transition"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => { setShowSavePreset(true); setPresetName(''); }}
                                    className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-teal-500 dark:hover:text-teal-400 transition"
                                >
                                    <Save className="w-3.5 h-3.5" />
                                    {language === 'fr' ? 'Sauvegarder cette configuration' : 'Save this configuration'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-xl p-3 text-sm text-red-600 dark:text-red-400">
                    {error}
                </div>
            )}

            {/* Results */}
            {searchResult && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                            <span className="font-bold text-gray-900 dark:text-white">{searchResult.totalQuestions}</span>
                            {' '}{language === 'fr' ? 'questions à réviser' : 'questions to revise'}
                            {searchResult.totalPages > 1 && (
                                <span className="text-gray-400"> — {searchResult.totalPages} {language === 'fr' ? 'examens' : 'exams'}</span>
                            )}
                        </p>
                        <button
                            onClick={() => setSearchResult(null)}
                            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                        >
                            {language === 'fr' ? 'Effacer' : 'Clear'}
                        </button>
                    </div>

                    {searchResult.totalQuestions === 0 ? (
                        <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center">
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                {language === 'fr'
                                    ? 'Aucune question à réviser pour ce filtre. Vous êtes à jour !'
                                    : 'No questions to revise for this filter. You\'re up to date!'}
                            </p>
                        </div>
                    ) : (
                        searchResult.pages.map((page, idx) => (
                            <div
                                key={idx}
                                className="bg-white dark:bg-[#121212] rounded-2xl p-4 md:p-5 border border-gray-200 dark:border-white/10 flex items-center justify-between group hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-all shadow-sm dark:shadow-none"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                                        {searchResult.totalPages > 1 ? `${idx + 1}/${searchResult.totalPages}` : (
                                            <Clock className="w-5 h-5" />
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="text-sm md:text-base font-bold text-gray-900 dark:text-white">
                                            {language === 'fr' ? 'Révision' : 'Revision'} — {getModeLabel()} — {getFilterLabel()}
                                            {searchResult.totalPages > 1 && (
                                                <span className="text-gray-400 font-normal ml-2">({idx + 1}/{searchResult.totalPages})</span>
                                            )}
                                        </h4>
                                        <p className="text-sm text-gray-500">
                                            {page.count} {language === 'fr' ? 'questions' : 'questions'}
                                            {' · '}{language === 'fr' ? 'Non comptabilisé' : 'Not scored'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleStartExam(idx)}
                                    disabled={loadingPageIdx === idx}
                                    className="px-5 py-2 rounded-xl font-bold text-sm text-white shadow-lg transition bg-teal-500 hover:bg-teal-600 disabled:opacity-60 flex items-center gap-2"
                                >
                                    {loadingPageIdx === idx ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : null}
                                    {language === 'fr' ? 'Commencer' : 'Start'}
                                </button>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Mode Selection Modal */}
            {showModeModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-800 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {language === 'fr' ? 'Choisir le mode' : 'Exam mode selection'}
                            </h3>
                            <button onClick={() => setShowModeModal(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition">
                                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={() => handleModeSelected('flash')}
                                className="w-full p-6 bg-emerald-500/10 hover:bg-emerald-500/20 border-2 border-emerald-500/20 hover:border-emerald-500 rounded-2xl transition text-left group"
                            >
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white">
                                        <LightningIcon className="w-6 h-6" />
                                    </div>
                                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                                        {language === 'fr' ? 'Mode question-réponse' : 'Flashcard Mode'}
                                    </span>
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    {language === 'fr'
                                        ? 'Obtenez la bonne réponse et l\'explication à la validation de chaque question.'
                                        : 'Receive the correct answer and explanation after validating each question.'}
                                </p>
                            </button>

                            <button
                                onClick={() => handleModeSelected('classic')}
                                className="w-full p-6 bg-blue-500/10 hover:bg-blue-500/20 border-2 border-blue-500/20 hover:border-blue-500 rounded-2xl transition text-left group"
                            >
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center text-white">
                                        <ClockIcon className="w-6 h-6" />
                                    </div>
                                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                                        {language === 'fr' ? 'Mode examen' : 'Exam Mode'}
                                    </span>
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    {language === 'fr'
                                        ? 'Validez toutes les questions avant d\'obtenir les bonnes réponses et les explications.'
                                        : 'Validate all the questions before receiving the correct answers and explanations.'}
                                </p>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RevisionExamSection;
